/**
 * <PlantaInterativa />
 * ---------------------------------------------------------------------------
 * Planta interativa estilo Next21 (https://ogud.co.jp/urbanex/next21/).
 *
 * UX MOBILE (alvo principal):
 * 1. INTRO: planta inteira COLORIDA visível, tagline embaixo, hint "Role pra explorar"
 * 2. SCROLL DOWN: planta gruda sticky no topo (40vh), cards passam por baixo
 * 3. CADA CARD no middle band → spotlight da área correspondente ativa
 *    - Planta inteira fica em outline cream, área ativa em cores vivas
 * 4. SCROLL UP: reseta pra base colorida quando nenhum card está no band
 *
 * ARQUITETURA DE IMAGENS (pipeline em SPEC_50_LP_SOFIA_CAMPOBELO.md):
 * - 1 PNG base (planta inteira colorida sobre transparente)
 * - 1 PNG spotlight por área (mesma silhueta, área X colorida, resto faded)
 * - Todas pixel-perfect alinhadas via composite Python (base.alpha + spot.RGB)
 * - Stack absoluto, opacity 1 só na ativa, transition 520ms ease
 *
 * IntersectionObserver:
 * - rootMargin -45%/-45% (middle 10% band) → trigger preciso
 * - Reseta pra null quando NENHUM card no band → mostra base
 *
 * ⚠ GOTCHA: position: sticky quebra com overflow-x:hidden/clip em ancestor.
 *   Fix scoped no SofiaLP.jsx via useEffect que override body.style.
 *
 * Template reutilizável pra outros clientes — replicar pipeline mantendo
 * mesma estrutura de stack + IO + intro spacer.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence, useMotionValue, useTransform, useMotionValueEvent } from 'framer-motion';
import { PLANTAS } from '../data/plantas';

const Section = styled.section`
  position: relative;
  width: 100%;
  background: #0a070b;
  color: #f5e8c8;
  padding: 20px 0 0;
  /* Clipa horizontalmente a planta scaled (overflow visual quando big) sem
     quebrar position:sticky — overflow-x:clip é safe pra sticky em ancestor,
     overflow:hidden NÃO é. */
  overflow-x: clip;

  /* Bridge com o EntornoMapa: sobe 120px pra dentro da seção anterior
     fadeando do map content pro #0a070b sólido. Curto pra não criar
     espaço preto gigante antes do header da planta. */
  &::before {
    content: '';
    position: absolute;
    top: -120px;
    left: 0;
    right: 0;
    height: 120px;
    background: linear-gradient(
      180deg,
      rgba(10, 7, 11, 0) 0%,
      rgba(10, 7, 11, 0.5) 40%,
      rgba(10, 7, 11, 0.9) 75%,
      #0a070b 100%
    );
    pointer-events: none;
  }
`;

const StickyTitle = styled.div`
  font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-weight: 400;
  font-size: 26px;
  line-height: 1;
  color: #faf3e0;
  text-align: center;
  margin-bottom: 10px;
  letter-spacing: 0.01em;
  /* Glow dourado — visível mas comportado pra não competir com a planta. */
  text-shadow:
    0 0 14px rgba(233, 200, 122, 0.45),
    0 0 28px rgba(233, 200, 122, 0.2);

  @media (min-width: 768px) {
    font-size: 32px;
  }
`;

const Tabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 18px;
  flex-wrap: nowrap;
  padding: 0 16px;
  max-width: 540px;
  margin: 0 auto;
`;

const Tab = styled.button`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  background: transparent;
  border: none;
  color: ${({ $active }) => ($active ? '#faf3e0' : 'rgba(245,232,200,0.45)')};
  padding: 6px 0 8px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.35 : 1)};
  position: relative;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 220ms ease;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%) scaleX(${({ $active }) => ($active ? 1 : 0)});
    transform-origin: center;
    bottom: 0;
    width: 70%;
    height: 1px;
    background: #e9c87a;
    transition: transform 280ms ease;
  }

  &:hover {
    color: ${({ $disabled }) => ($disabled ? null : '#faf3e0')};
  }
`;

const TabSuffix = styled.span`
  color: ${({ $active }) => ($active ? 'rgba(233,200,122,0.85)' : 'rgba(245,232,200,0.35)')};
  margin-left: 6px;
  font-size: 9px;
  letter-spacing: 0.3em;
`;

const StickyWrap = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  background: rgba(10, 7, 11, 0.78);
  padding: 6px 16px 8px;
  backdrop-filter: blur(20px) saturate(140%);
  -webkit-backdrop-filter: blur(20px) saturate(140%);
  border-bottom: 1px solid rgba(245, 232, 200, 0.06);
`;

const PlantaFrame = styled.div`
  position: relative;
  width: 100%;
  /* Mobile: menor pra dar mais espaço pras fotos no feed. Safari come o
     bottom com a URL bar, então cada vh economizado aqui ajuda lá. */
  max-width: 210px;
  margin: 0 auto;
  aspect-ratio: ${({ $aspect }) => $aspect};
  background: transparent;

  @media (min-width: 768px) {
    max-width: 280px;
  }
`;

const PlantaImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 520ms ease;
`;

const PinButton = styled.button`
  position: absolute;
  width: 36px;
  height: 36px;
  margin: -18px 0 0 -18px;
  top: ${({ $y }) => $y}%;
  left: ${({ $x }) => $x}%;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  z-index: 3;

  &::before {
    content: '';
    position: absolute;
    inset: 11px;
    border-radius: 50%;
    background: #2a1d0f;
    border: 2px solid #e9c87a;
    box-shadow:
      0 0 0 3px rgba(245, 232, 216, 0.95),
      0 4px 10px rgba(0, 0, 0, 0.35);
    transition: transform 260ms ease, box-shadow 260ms ease, background 260ms ease;
  }

  ${({ $active }) =>
    $active &&
    `
    &::before {
      transform: scale(1.45);
      background: #c89048;
      border-color: #faf3e0;
      box-shadow:
        0 0 0 4px rgba(245, 232, 216, 1),
        0 0 0 8px rgba(200, 144, 72, 0.35),
        0 6px 18px rgba(0, 0, 0, 0.45);
    }
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: radial-gradient(circle at center, rgba(200,144,72,0.45) 0%, rgba(200,144,72,0) 65%);
      animation: pinPulse 1.6s ease-out infinite;
      pointer-events: none;
    }
    @keyframes pinPulse {
      0% { transform: scale(0.8); opacity: 0.8; }
      100% { transform: scale(2.6); opacity: 0; }
    }
  `}
`;

const StickyMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 4px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 11px;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: rgba(245, 232, 200, 0.7);
`;

const StickyMetaLabel = styled.span`
  /* flex item — comporta nome + metragem; deixa quebrar se ficar largo */
  text-align: center;
  flex: 1 1 auto;
  min-width: 0;
  padding: 0 4px;
`;

const StickyMetragem = styled.span`
  color: #e9c87a;
  font-weight: 500;
  margin-left: 8px;
`;

const NavArrow = styled.button`
  background: transparent;
  border: 1px solid rgba(245, 232, 200, 0.25);
  color: ${({ $disabled }) => ($disabled ? 'rgba(245,232,200,0.2)' : 'rgba(245,232,200,0.75)')};
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 220ms ease;
  flex-shrink: 0;
  padding: 0;

  &:hover {
    border-color: ${({ $disabled }) => ($disabled ? null : 'rgba(250,215,140,0.7)')};
    color: ${({ $disabled }) => ($disabled ? null : '#faf3e0')};
  }
`;

const MorphSpacer = styled.div`
  /* Cria scroll de janela DENTRO da Section pra animar a planta de big → small.
     StickyWrap continua pinned durante esse scroll (sticky scope = Section).
     Showcases entra em viewport só DEPOIS desse spacer → evita overlap com
     planta scaled-up. Altura = morphDistance usado no scroll handler abaixo.
     14vh/22vh: encurtado pra primeira foto do Showcase aparecer parcialmente
     no viewport inicial (em vez de 411px de preto vazio). */
  height: 14vh;
  pointer-events: none;

  @media (min-width: 768px) {
    height: 22vh;
  }
`;

const Showcases = styled.div`
  /* Feed-style scroll container — scoped à seção, não polui o html.
     Cada AreaShowcase preenche 100% deste container; scroll-snap faz
     navegação tipo IG Reels. Sticky planta acima continua no scroll
     normal da página.
     dvh (não vh) pra acomodar a barra de URL do Safari mobile que
     muda dinamicamente — sem dvh a foto fica cortada quando a barra
     volta. Sticky encolhida → mais espaço pra foto (68 em vez de 62). */
  position: relative;
  height: calc(100dvh - 32vh);
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  /* REGRA #1 (SPEC 62): o feed CONSOME o scroll internamente até o user passar
     por todas as fotos da última planta. overscroll-behavior: contain impede o
     scroll de "vazar" pra página (= pular pro condomínio antes da hora) e o snap
     mandatory mantém a navegação card-a-card. Quando $released (user chegou no
     último showcase da última planta), soltamos os dois → o próximo gesto pra
     baixo rola a página naturalmente pro <CondominioScrub />. */
  overscroll-behavior-y: ${({ $released }) => ($released ? 'auto' : 'contain')};
  scroll-snap-type: ${({ $released }) => ($released ? 'none' : 'y mandatory')};
`;

const AreaShowcase = styled.article`
  position: relative;
  width: 100%;
  height: 100%; /* 100% do feed container = 62vh */
  overflow: hidden;
  background: #0a070b;
  scroll-snap-align: start;
  scroll-snap-stop: always;
`;

const PhotoLayer = styled.div`
  /* Foto cover — preenche o container do showcase. Crop natural nas
     laterais (~20%) cria sensação de "zoom adequado". User pediu pra
     ter leve zoom em vez de muito espaço de blur. */
  position: absolute;
  inset: 0;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center;

  /* SPEC 64 #B — CROSSOVER com o vídeo do condomínio (só a última foto da
     última planta = living da Premium 328). O <CondominioScrub /> mostra o
     vídeo em object-fit:cover de TELA CHEIA (a imagem 9:16 ajusta por ALTURA,
     cortando as laterais e centralizando) clipado à faixa de baixo. O cover
     padrão deste card (caixa 68vh) ajusta por LARGURA (corta topo/baixo) → o
     eixo do crop inverte e o swap foto→vídeo "pula". Aqui replicamos EXATO o
     enquadramento do vídeo: altura = tela inteira, ancorado no rodapé, pra a
     faixa visível do card bater pixel-a-pixel com a faixa revelada do vídeo. */
  ${({ $crossover }) =>
    $crossover &&
    `
    background-size: auto 100dvh;
    background-position: center bottom;
    @supports not (height: 100dvh) {
      background-size: auto 100vh;
    }
  `}
`;

const PhotoRail = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const PhotoSlide = styled.div`
  flex: 0 0 100%;
  height: 100%;
  scroll-snap-align: start;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center;
`;

const ShowcaseOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  /* bottom extra reserva espaço pro botão flutuante não tampar a tagline. */
  padding: 40px 20px 88px;
  background: linear-gradient(
    180deg,
    rgba(10, 7, 11, 0) 0%,
    rgba(10, 7, 11, 0.55) 60%,
    rgba(10, 7, 11, 0.9) 100%
  );
  color: #faf3e0;
  pointer-events: none;
`;

const ShowcaseEyebrow = styled.div`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 9px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: #e9c87a;
  opacity: 0.95;
  margin-bottom: 8px;
`;

const ShowcaseTitle = styled.h3`
  font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-weight: 400;
  font-size: clamp(26px, 7vw, 40px);
  line-height: 1;
  margin: 0 0 8px;
  color: #faf3e0;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.6);
`;

const ShowcaseTagline = styled.p`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12px;
  line-height: 1.45;
  letter-spacing: 0.02em;
  color: rgba(245, 232, 200, 0.85);
  margin: 0;
  max-width: 30em;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
`;

const SwipeHint = styled.div`
  position: absolute;
  top: 18px;
  right: 18px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 9px;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: rgba(245, 232, 200, 0.85);
  background: rgba(10, 7, 11, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  padding: 6px 12px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 6px;
  pointer-events: none;
`;

export default function PlantaInterativa() {
  const [activePlantaId, setActivePlantaId] = useState(PLANTAS[0].id);
  const activePlanta = useMemo(
    () => PLANTAS.find((p) => p.id === activePlantaId) || PLANTAS[0],
    [activePlantaId]
  );

  const [activeAreaId, setActiveAreaId] = useState(null);
  const cardRefs = useRef({});
  const intersectingMap = useRef({});
  const sectionRef = useRef(null);
  const stickyRef = useRef(null);
  const showcasesRef = useRef(null);

  // REGRA #1 (SPEC 62): só libera o overflow do feed pra fora da seção (= deixa
  // a página rolar pro <CondominioScrub />) quando o user está na ÚLTIMA planta
  // E no fundo do feed (passou por todas as fotos). overflowReleasedRef espelha
  // o state pra ser lido sincronamente dentro dos listeners de scroll.
  const [overflowReleased, setOverflowReleased] = useState(false);
  const overflowReleasedRef = useRef(false);
  const setReleased = (v) => {
    if (overflowReleasedRef.current === v) return;
    overflowReleasedRef.current = v;
    setOverflowReleased(v);
  };

  // Morph scroll-driven: planta começa BIG (entrando na seção) e encolhe pro
  // tamanho final conforme o user rola pelo MorphSpacer. Driver = manual scroll
  // listener (mais previsível que useScroll offsets, evita feedback loop porque
  // só animamos transform: scale — layout permanece estável).
  // matchMedia → translateY do meta varia por breakpoint (planta visual cresce
  // mais no desktop, então meta precisa descer mais pra ficar abaixo dela).
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px)');
    setIsDesktop(mql.matches);
    const handler = (e) => setIsDesktop(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const morphProgress = useMotionValue(0);
  // Planta menor no big state (1.4 em vez de 1.7 original) pra reduzir preto
  // vazio embaixo. Big visual continua claro mas ganha respiração no viewport
  // e o morph encolhe menos → fica fluido mesmo com runway de scroll curto.
  const plantaScale = useTransform(morphProgress, [0, 1], [1.4, 1]);
  // Título "Plantas" também acompanha o morph — começa maior e encolhe pro
  // tamanho final junto com a planta. Visível desde o start (sem fade).
  const titleScale = useTransform(morphProgress, [0, 1], [1.8, 1]);
  const tabsOpacity = useTransform(morphProgress, [0.6, 1], [0, 1]);
  // Meta (setinhas + metragem) visível desde o start. translateY empurra ela
  // pra abaixo da planta scaled-up no big state (planta visual cresce ~144px
  // mobile / ~200px desktop além do layout box). Volta a 0 quando scale=1.
  const metragemScale = useTransform(morphProgress, [0, 1], [1.5, 1]);
  const nomeOpacity = useTransform(morphProgress, [0.6, 1], [0, 1]);
  const metaTranslateY = useTransform(
    morphProgress,
    [0, 1],
    isDesktop ? [120, 0] : [88, 0]
  );

  // showNome controla se o nome da planta ("Tipo Final 2") está no DOM. Quando
  // false (big state), só metragem é renderizada → fica centralizada de
  // verdade. Quando true (small state), nome aparece à esquerda da metragem.
  const [showNome, setShowNome] = useState(false);
  useMotionValueEvent(morphProgress, 'change', (v) => {
    setShowNome(v > 0.65);
  });

  useEffect(() => {
    let raf = 0;
    const compute = () => {
      raf = 0;
      const sec = sectionRef.current;
      if (!sec) return;
      const rect = sec.getBoundingClientRect();
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;
      // morphDistance == altura do MorphSpacer (14vh mobile / 22vh desktop).
      // Mantém sincronizado: planta termina de encolher exatamente quando o
      // spacer acabou de rolar e Showcases encosta no rodapé do sticky.
      const morphDist = window.innerHeight * (isDesktop ? 0.22 : 0.14);
      const p = Math.max(0, Math.min(1, -rect.top / morphDist));
      morphProgress.set(p);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', compute);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [morphProgress]);

  // Lista de plantas habilitadas (filtra disabled) → arrow nav
  const enabledPlantas = useMemo(() => PLANTAS.filter((p) => p.enabled), []);
  const currentIdx = enabledPlantas.findIndex((p) => p.id === activePlantaId);
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx < enabledPlantas.length - 1;

  // Direção da última troca (-1 prev, +1 next) — pra animar slide horizontal direcional
  const [navDirection, setNavDirection] = useState(1);
  // Quando user volta pra planta anterior VIA SCROLL UP (não via setinha/tab),
  // o feed da nova planta começa no ÚLTIMO card — continuação natural do gesto
  // "estou subindo". Ref pra atravessar o async do setState e ser lido pelo
  // useEffect após o render da nova planta.
  const scrollFeedToEndRef = useRef(false);

  const changePlanta = (newId, dir = 1, scrollToEnd = false) => {
    scrollFeedToEndRef.current = scrollToEnd;
    // Trocou de planta → re-contém o feed (a nova planta volta a consumir scroll).
    setReleased(false);
    setNavDirection(dir);
    setActivePlantaId(newId);
    setActiveAreaId(null);
    // Scroll pra o topo da sticky bar (não pro header "O apartamento / Plantas")
    if (stickyRef.current) {
      stickyRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Reset do feed acontece no useEffect abaixo — precisa esperar o render
    // da nova planta pra scrollHeight refletir os cards corretos.
  };

  // Reset do scroll interno do feed após troca de planta. Lê scrollFeedToEndRef
  // pra decidir top:0 (primeira área) vs scrollHeight (última área).
  useEffect(() => {
    const sc = showcasesRef.current;
    if (!sc) return;
    if (scrollFeedToEndRef.current) {
      sc.scrollTo({ top: sc.scrollHeight, behavior: 'auto' });
    } else {
      sc.scrollTo({ top: 0, behavior: 'auto' });
    }
    scrollFeedToEndRef.current = false;
  }, [activePlantaId]);
  const onPrev = () => { if (hasPrev) changePlanta(enabledPlantas[currentIdx - 1].id, -1); };
  const onNext = () => { if (hasNext) changePlanta(enabledPlantas[currentIdx + 1].id, +1); };

  // Auto-advance entre plantas: quando user chega no último card da planta atual
  // e tenta rolar pra baixo (wheel ou swipe up), avança pra próxima planta.
  // Mesma lógica pra cima: primeiro card + swipe down → planta anterior.
  // Snap mandatory bloqueia o scroll visual, mas intercepta o gesto.
  useEffect(() => {
    const sc = showcasesRef.current;
    if (!sc) return;
    let touchStartY = null;
    let startAtBottom = false;
    let startAtTop = false;
    let lastTrigger = 0;

    // Borda do feed pelo ÍNDICE do card (snap), não por pixel: Math.round acha o
    // card mais próximo mesmo com o snap ainda settlando → não falha quando o
    // user desliza logo que chega no último card.
    const edgeState = () => {
      const h = sc.clientHeight || 1;
      const idx = Math.round(sc.scrollTop / h);
      const last = Math.max(0, Math.round(sc.scrollHeight / h) - 1);
      return { atBottom: idx >= last, atTop: idx <= 0 };
    };

    const doAdvance = (dir) => {
      const now = Date.now();
      if (now - lastTrigger < 600) return;
      if (dir > 0 && hasNext) {
        lastTrigger = now;
        changePlanta(enabledPlantas[currentIdx + 1].id, +1);
      } else if (dir < 0 && hasPrev) {
        lastTrigger = now;
        // scrollToEnd=true → feed da planta anterior começa no último card
        changePlanta(enabledPlantas[currentIdx - 1].id, -1, true);
      }
    };

    const onWheel = (e) => {
      const { atBottom, atTop } = edgeState();
      if (e.deltaY > 24 && atBottom) doAdvance(+1);
      else if (e.deltaY < -24 && atTop) doAdvance(-1);
    };
    // A INTENÇÃO de avançar só existe se o user JÁ estava na borda quando começou
    // o gesto. Checar no touchSTART (não no touchend) elimina o "pulou a área 3"
    // e a falha de avançar quando o snap ainda não bateu no fundo.
    const onTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
      const s = edgeState();
      startAtBottom = s.atBottom;
      startAtTop = s.atTop;
    };
    const onTouchEnd = (e) => {
      if (touchStartY === null) return;
      const dy = touchStartY - e.changedTouches[0].clientY;
      touchStartY = null;
      if (Math.abs(dy) < 36) return;
      if (dy > 0 && startAtBottom) doAdvance(+1);      // já no fundo + arrasta p/ cima
      else if (dy < 0 && startAtTop) doAdvance(-1);    // já no topo + arrasta p/ baixo
    };

    sc.addEventListener('wheel', onWheel, { passive: true });
    sc.addEventListener('touchstart', onTouchStart, { passive: true });
    sc.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      sc.removeEventListener('wheel', onWheel);
      sc.removeEventListener('touchstart', onTouchStart);
      sc.removeEventListener('touchend', onTouchEnd);
    };
  }, [activePlantaId, currentIdx, hasNext, hasPrev, enabledPlantas]);

  // REGRA #1 (SPEC 62): libera o overflow pro condomínio só na ÚLTIMA planta,
  // quando o feed chega no fundo (= user passou por todas as fotos). Em qualquer
  // outra planta, o feed permanece sempre contido. Usa o evento `scroll` do
  // próprio feed (dispara no iOS, ≠ `wheel`) — rAF-throttled.
  useEffect(() => {
    const sc = showcasesRef.current;
    if (!sc) return;
    // Plantas intermediárias nunca liberam — o overflow chega no condomínio só
    // depois da última planta (Premium 328m²).
    if (hasNext) {
      setReleased(false);
      return;
    }
    let raf = 0;
    const check = () => {
      raf = 0;
      const atBottom = sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 12;
      setReleased(atBottom);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(check);
    };
    sc.addEventListener('scroll', onScroll, { passive: true });
    check(); // estado inicial (feed começa no topo → contido)
    return () => {
      sc.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [activePlantaId, hasNext]);

  // REGRA #1 (SPEC 62) — TRAVA DURA de scroll da PÁGINA.
  // overscroll-behavior/snap só blindam quando o dedo está sobre o feed. Mas o
  // user podia arrastar no cabeçalho da planta (ou dar um flick forte) e a
  // página inteira rolava direto pro condomínio, pulando as fotos. Aqui a gente
  // GRAMPEIA a window: enquanto não liberado, ela não passa do fim da seção das
  // plantas (clampMax = bottom - innerHeight) → o condomínio ("Da sua varanda")
  // fica fisicamente inalcançável até o user chegar no fim do feed da última
  // planta. O feed tem scroll próprio (Showcases) e não é afetado por isso.
  // Só libera quando overflowReleasedRef vira true (fundo da Premium 328m²).
  useEffect(() => {
    const clampMaxY = () => {
      const sec = sectionRef.current;
      if (!sec) return Infinity;
      const rect = sec.getBoundingClientRect();
      const sectionTop = rect.top + window.scrollY;
      // -2px de folga pra garantir que nem 1 sliver do condomínio apareça no
      // limite (a borda cairia exatamente na dobra do viewport sem isso).
      return sectionTop + sec.offsetHeight - window.innerHeight - 2;
    };

    // SPEC 64 #1 — fade-out RÁPIDO do header das plantas no handoff. O StickyWrap
    // é `sticky top:0` e só desgrudaria lá no fim da seção, ficando pinado e
    // vazando atrás do topo TRANSPARENTE do reveal do condomínio durante quase
    // uma viewport ("a parte de cima demora a sair"). Aqui dirigimos a opacidade
    // pela POSIÇÃO da janela (não pelo flag `released`) — começa a sumir assim
    // que a janela passa do fim das plantas (= começo do FILL) e some em ~0.3
    // viewport. Dirigir por posição (não por flag) garante que, ao voltar pra
    // cima pras plantas, o header reaparece sozinho. Imperativo via ref pra não
    // re-renderizar a cada frame de scroll.
    const HEADER_FADE_DIST = 0.3; // fração da tela pro header sumir de vez
    const applyHeaderFade = (fade) => {
      const el = stickyRef.current;
      if (!el) return;
      el.style.opacity = String(1 - fade);
      el.style.transform = fade > 0 ? `translateY(${-fade * 22}px)` : '';
      el.style.pointerEvents = fade > 0.5 ? 'none' : '';
    };

    // Grampeia momentum/flick que vazou pra página (desktop + iOS best-effort).
    const onScroll = () => {
      const max = clampMaxY();
      if (!overflowReleasedRef.current) {
        if (window.scrollY > max) window.scrollTo(0, max);
        applyHeaderFade(0); // ainda preso nas plantas → header cheio
        return;
      }
      // Liberado: o handoff começou. `over` = quanto a janela já passou do fim
      // das plantas (≡ `enter` do FILL no CondominioScrub).
      const over = window.scrollY - max;
      const fade = Math.max(
        0,
        Math.min(1, over / (window.innerHeight * HEADER_FADE_DIST))
      );
      applyHeaderFade(fade);
    };

    // iOS: scrollTo durante momentum às vezes não segura. Aqui barramos o
    // arrasto da PÁGINA na raiz — mas SÓ na direção que levaria pro condomínio
    // (dedo subindo = página descendo). Subir de volta pro hero fica LIVRE.
    // Dentro do feed (Showcases) rola normal; swipe horizontal de troca de
    // planta usa touchstart/touchend, então não quebra.
    let lastTouchY = null;
    const onTouchStart = (e) => {
      lastTouchY = e.touches[0] ? e.touches[0].clientY : null;
    };
    const onTouchMove = (e) => {
      const y = e.touches[0] ? e.touches[0].clientY : null;
      const prev = lastTouchY;
      lastTouchY = y;
      if (overflowReleasedRef.current) return;
      const sc = showcasesRef.current;
      if (sc && (sc === e.target || sc.contains(e.target))) return;
      if (y == null || prev == null) return;
      const dy = y - prev; // dy < 0 → dedo subindo → página desceria (rumo ao condomínio)
      if (dy < 0 && window.scrollY >= clampMaxY() - 1) {
        e.preventDefault();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, []);

  // IntersectionObserver: ativa card no middle band; reseta pra null quando NENHUM
  // card está no band — assim usuário vê a planta BASE inteira no intro/scroll inicial.
  useEffect(() => {
    intersectingMap.current = {};
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const id = e.target.dataset.areaId;
          if (!id) return;
          if (e.isIntersecting) {
            intersectingMap.current[id] = e.intersectionRatio;
          } else {
            delete intersectingMap.current[id];
          }
        });
        const ids = Object.keys(intersectingMap.current);
        if (ids.length === 0) {
          setActiveAreaId(null);
        } else {
          const best = ids.reduce((a, b) =>
            intersectingMap.current[a] >= intersectingMap.current[b] ? a : b
          );
          setActiveAreaId(best);
        }
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    Object.values(cardRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [activePlantaId]);


  const onPinClick = (areaId) => {
    const el = cardRefs.current[areaId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onTabClick = (planta) => {
    if (!planta.enabled) return;
    const targetIdx = enabledPlantas.findIndex((p) => p.id === planta.id);
    const dir = targetIdx >= currentIdx ? 1 : -1;
    changePlanta(planta.id, dir);
  };

  // Variants: slide horizontal direcional + fade — premium ease
  const plantaVariants = {
    enter: (dir) => ({ x: dir > 0 ? 48 : -48, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -48 : 48, opacity: 0 }),
  };

  // Swipe horizontal na planta → troca pra planta vizinha (gesto mobile).
  // touchStart guarda x/y inicial; touchEnd compara delta.
  // Threshold: dx > 50px AND |dx| > |dy|*1.5 pra evitar conflito com scroll vertical.
  const touchStartRef = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const start = touchStartRef.current;
    if (!start) return;
    const end = e.changedTouches[0];
    const dx = end.clientX - start.x;
    const dy = end.clientY - start.y;
    touchStartRef.current = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0 && hasNext) onNext();
      else if (dx > 0 && hasPrev) onPrev();
    }
  };

  return (
    <Section ref={sectionRef}>
      {activePlanta.enabled && activePlanta.image && (
        <>
          <StickyWrap ref={stickyRef}>
            <motion.div style={{ scale: titleScale, transformOrigin: '50% 50%' }}>
              <StickyTitle>Plantas</StickyTitle>
            </motion.div>
            <motion.div style={{ opacity: tabsOpacity }}>
              <Tabs>
                {PLANTAS.filter((p) => p.enabled).map((p) => {
                  const active = p.id === activePlantaId;
                  return (
                    <Tab
                      key={p.id}
                      $active={active}
                      onClick={() => onTabClick(p)}
                      aria-label={`${p.tab} · ${p.metragem}`}
                    >
                      {p.metragem}
                      {p.tabSuffix && <TabSuffix $active={active}>{p.tabSuffix}</TabSuffix>}
                    </Tab>
                  );
                })}
              </Tabs>
            </motion.div>
            <AnimatePresence mode="wait" initial={false} custom={navDirection}>
              <motion.div
                key={activePlanta.id}
                custom={navDirection}
                variants={plantaVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                style={{
                  touchAction: 'pan-y',
                  scale: plantaScale,
                  transformOrigin: '50% 0%',
                }}
              >
                <PlantaFrame $aspect={activePlanta.imageAspect || 4 / 3}>
                  {/* Base = planta clean (sem nenhuma área destacada) */}
                  <PlantaImg
                    src={activePlanta.image}
                    alt={`Planta ${activePlanta.metragem}`}
                    $active={!activeAreaId || !activePlanta.areas.some((a) => a.id === activeAreaId && a.spotlight)}
                  />
                  {/* Spotlight stack: 1 imagem por área, opacity 1 só na ativa */}
                  {activePlanta.areas
                    .filter((a) => a.spotlight)
                    .map((a) => (
                      <PlantaImg
                        key={a.id}
                        src={a.spotlight}
                        alt=""
                        aria-hidden="true"
                        $active={a.id === activeAreaId}
                      />
                    ))}
                  {activePlanta.areas.map((a) => (
                    <PinButton
                      key={a.id}
                      $x={a.pin.x}
                      $y={a.pin.y}
                      $active={a.id === activeAreaId}
                      onClick={() => onPinClick(a.id)}
                      aria-label={a.nome}
                    />
                  ))}
                </PlantaFrame>
              </motion.div>
            </AnimatePresence>
            <motion.div style={{ y: metaTranslateY }}>
              <StickyMeta>
                <NavArrow
                  $disabled={!hasPrev}
                  onClick={onPrev}
                  aria-label="Planta anterior"
                  disabled={!hasPrev}
                >
                  ◀
                </NavArrow>
                <StickyMetaLabel>
                  {showNome && (
                    <motion.span
                      key="nome"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.25 }}
                      style={{ opacity: nomeOpacity }}
                    >
                      {activePlanta.nome}
                    </motion.span>
                  )}
                  <motion.span
                    style={{
                      scale: metragemScale,
                      display: 'inline-block',
                      transformOrigin: '50% 50%',
                    }}
                  >
                    <StickyMetragem style={{ marginLeft: showNome ? 8 : 0 }}>
                      {activePlanta.metragem}
                    </StickyMetragem>
                  </motion.span>
                </StickyMetaLabel>
                <NavArrow
                  $disabled={!hasNext}
                  onClick={onNext}
                  aria-label="Próxima planta"
                  disabled={!hasNext}
                >
                  ▶
                </NavArrow>
              </StickyMeta>
            </motion.div>
          </StickyWrap>

          <MorphSpacer aria-hidden="true" />

          <Showcases ref={showcasesRef} $released={overflowReleased}>
            {activePlanta.areas.map((a, i) => {
              const fotos = a.fotos || [];
              const tagline = (a.descricao || '').split(/\.\s/)[0].replace(/\.$/, '');
              const multi = fotos.length > 1;
              return (
                <AreaShowcase
                  key={a.id}
                  data-area-id={a.id}
                  ref={(el) => (cardRefs.current[a.id] = el)}
                >
                  {multi ? (
                    <PhotoRail>
                      {fotos.map((src, idx) => (
                        <PhotoSlide key={idx} $src={src} />
                      ))}
                    </PhotoRail>
                  ) : (
                    fotos[0] && (
                      <PhotoLayer
                        $src={fotos[0]}
                        $crossover={!hasNext && i === activePlanta.areas.length - 1}
                      />
                    )
                  )}
                  {multi && (
                    <SwipeHint>
                      <span>{fotos.length} fotos</span>
                      <span>→</span>
                    </SwipeHint>
                  )}
                  <ShowcaseOverlay>
                    <ShowcaseEyebrow>
                      Área {String(i + 1).padStart(2, '0')} / {String(activePlanta.areas.length).padStart(2, '0')}
                    </ShowcaseEyebrow>
                    <ShowcaseTitle>{a.nome}</ShowcaseTitle>
                    <ShowcaseTagline>{tagline}</ShowcaseTagline>
                  </ShowcaseOverlay>
                </AreaShowcase>
              );
            })}
          </Showcases>
        </>
      )}
    </Section>
  );
}
